import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { interval, Subscription, timer } from 'rxjs';
import { filter, skip } from 'rxjs/operators';
import { BaseDialogComponent } from 'src/app/shared/base/dialog/dialog.component';
import { DownloadService } from 'src/app/service/download.service';
import { FileDownloadService } from 'src/app/service/file-download.service';
import { HubMessage, HubMessageType, HubService } from 'src/app/service/hub.service';
import { ForensicNotificationServics } from 'src/app/service/forensic-notification.servics';
import { protectedResources } from 'src/app/auth-config';
import URLUtils from 'src/app/shared/Utility/urlUtils';
import { NotebookDownloadTracker } from './notebook-download-tracker.service';
import { NotebookDownloadTelemetry } from './notebook-download-telemetry.service';
import {
  DOWNLOAD_DIALOG_WIDTH,
  findNotebookNotificationResult,
  formatElapsed,
  formatFileNameDate,
  formatMegabytes,
  initialNotebookDownloadState,
  isAwaitingResult,
  matchNotebookResult,
  newRequestId,
  NOTEBOOK_DONE_AUTO_CLOSE_MS,
  NOTEBOOK_GENERATION_TIMEOUT_MS,
  NOTEBOOK_NOTIFICATION_POLL_COUNT,
  NOTEBOOK_STATUS_POLL_INTERVAL_MS,
  NotebookDownloadEvent,
  NotebookDownloadState,
  NotebookDownloadStep,
  NotebookFileType,
  NotebookResultTarget,
  reduceNotebookDownload,
} from './notebook-download-state';

const ELAPSED_TICK_MS = 1000;

export interface NotebookDownloadParams {
  notebookId: string;
  fileType: NotebookFileType;
  packageType: string;
  password: string;
  userId: string;
  /** Optional, shown in the dialog subtitle only. */
  notebookName?: string;
}

interface StepView {
  key: NotebookDownloadStep;
  label: string;
}

@Component({
  selector: 'app-notebook-download-stepper',
  templateUrl: './notebook-download-stepper.component.html',
  styleUrls: ['./notebook-download-stepper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotebookDownloadStepperComponent extends BaseDialogComponent implements OnDestroy {
  readonly steps: StepView[] = [
    { key: 'requesting', label: 'Request' },
    { key: 'generating', label: 'Generate' },
    { key: 'downloading', label: 'Download' },
    { key: 'done', label: 'Done' },
  ];

  readonly dialogWidth = DOWNLOAD_DIALOG_WIDTH;

  state: NotebookDownloadState | null = null;
  elapsedText = formatElapsed(0);
  showDetails = false;

  private params: NotebookDownloadParams | null = null;
  private job: Subscription | null = null;
  private knownNotificationIds: Set<number> | null = null;
  private pollInFlight = false;
  private reportedUnmatchedRequestIds = new Set<string>();
  private reportedFallbackMatch = false;

  constructor(
    public readonly cdRef: ChangeDetectorRef,
    private readonly downloadService: DownloadService,
    private readonly fileDownloadService: FileDownloadService,
    private readonly hubService: HubService,
    private readonly notificationService: ForensicNotificationServics,
    private readonly tracker: NotebookDownloadTracker,
    private readonly telemetry: NotebookDownloadTelemetry
  ) {
    super(cdRef);
  }

  get fileTypeLabel(): string {
    return this.state ? this.state.fileType : '';
  }

  /** Format shown under the title: "PDF" or "Full-Disclosure ZIP". */
  get formatLabel(): string {
    if (!this.state) {
      return '';
    }
    return this.state.fileType === 'PDF' ? 'PDF' : 'Full-Disclosure ZIP';
  }

  get notebookName(): string {
    return this.params && this.params.notebookName ? this.params.notebookName.trim() : '';
  }

  /** Single-line subtitle: format, then the notebook name when known. */
  get subtitle(): string {
    return this.notebookName ? this.formatLabel + ' · ' + this.notebookName : this.formatLabel;
  }

  get progressPercent(): number {
    if (!this.state || !this.state.totalBytes) {
      return 0;
    }
    return Math.min(100, Math.round((this.state.loadedBytes / this.state.totalBytes) * 100));
  }

  get loadedMegabytes(): string {
    return formatMegabytes(this.state ? this.state.loadedBytes : 0);
  }

  get totalMegabytes(): string | null {
    return this.state && this.state.totalBytes ? formatMegabytes(this.state.totalBytes) : null;
  }

  get errorHeadline(): string {
    switch (this.state && this.state.errorCode) {
      case 'REQUEST_FAILED':
        return 'The request could not be sent.';
      case 'GENERATION_FAILED':
        return 'The ' + this.fileTypeLabel + ' could not be generated.';
      case 'TIMEOUT':
        return 'This is taking longer than expected.';
      case 'DOWNLOAD_FAILED':
        return 'The download did not finish.';
      default:
        return 'Something went wrong.';
    }
  }

  stepStatus(step: NotebookDownloadStep): 'complete' | 'active' | 'pending' | 'failed' {
    if (!this.state) {
      return 'pending';
    }
    const order = this.steps.map((s) => s.key);
    const current = this.state.step === 'error' ? this.state.failedStep || 'requesting' : this.state.step;
    const stepIndex = order.indexOf(step);
    const currentIndex = order.indexOf(current);
    if (stepIndex < currentIndex || this.state.step === 'done') {
      return 'complete';
    }
    if (stepIndex === currentIndex) {
      return this.state.step === 'error' ? 'failed' : 'active';
    }
    return 'pending';
  }

  start(params: NotebookDownloadParams): void {
    this.disposeJob();
    this.params = params;
    this.showDetails = false;
    this.beginJob();
    this.show();
  }

  retry(): void {
    if (!this.params) {
      return;
    }
    this.disposeJob();
    this.showDetails = false;
    this.beginJob();
    this.cdRef.detectChanges();
  }

  close(): void {
    this.hide();
    this.releaseUnlessDownloading();
  }

  private finish(): void {
    this.disposeJob();
    this.hide();
  }

  toggleDetails(): void {
    this.showDetails = !this.showDetails;
    this.cdRef.detectChanges();
  }

  onDialogClose(event: any): void {
    super.onDialogClose(event);
    this.releaseUnlessDownloading();
  }

  onOverlayClickOutsideMenu(): void {
    this.close();
  }

  ngOnDestroy(): void {
    this.disposeJob();
    this.params = null;
    super.ngOnDestroy();
  }

  protected now(): number {
    return Date.now();
  }

  protected randomBytes(bytes: Uint8Array): Uint8Array {
    return crypto.getRandomValues(bytes);
  }

  private get target(): NotebookResultTarget {
    return { requestId: this.state.requestId, notebookId: this.state.notebookId, fileType: this.state.fileType };
  }

  private beginJob(): void {
    const params = this.params;
    const requestId = newRequestId((bytes) => this.randomBytes(bytes));
    this.state = initialNotebookDownloadState(requestId, params.notebookId, params.fileType, this.now());
    this.elapsedText = formatElapsed(0);
    this.knownNotificationIds = null;
    this.pollInFlight = false;
    this.reportedFallbackMatch = false;
    this.reportedUnmatchedRequestIds.clear();
    this.tracker.register(this.target);

    const job = new Subscription();
    this.job = job;

    job.add(
      this.hubService.hub$
        .pipe(filter(({ type }) => type === HubMessageType.NotebookGenerated || type === HubMessageType.NotebookGenerationFailed))
        .subscribe((message) => this.onHubMessage(message))
    );
    job.add(this.hubService.connected$.pipe(skip(1), filter((connected) => connected)).subscribe(() => this.pollNow()));
    job.add(interval(ELAPSED_TICK_MS).subscribe(() => this.tick()));
    job.add(interval(NOTEBOOK_STATUS_POLL_INTERVAL_MS).subscribe(() => this.pollNow()));
    job.add(timer(NOTEBOOK_GENERATION_TIMEOUT_MS).subscribe(() => this.apply({ type: 'timedOut' })));
    job.add(
      this.notificationService.GetAllNotification(NOTEBOOK_NOTIFICATION_POLL_COUNT, false, params.userId).subscribe({
        next: (response) => {
          this.knownNotificationIds = new Set((response.result || []).map((n) => n.notificationID));
        },
        error: () => {
          this.knownNotificationIds = null;
        },
      })
    );
    job.add(
      this.downloadService
        .GenerateDownloadNotebook(params.notebookId, params.fileType === 'PDF' ? 'application/pdf' : 'application/zip', params.password, params.packageType, params.userId, requestId)
        .subscribe({
          next: (body) => this.apply({ type: 'requestAccepted', requestId: this.readRequestId(body) }),
          error: (error: HttpErrorResponse) => this.apply({ type: 'requestFailed', httpStatus: error.status }),
        })
    );
  }

  private releaseUnlessDownloading(): void {
    if (this.state && this.state.step === 'downloading') {
      this.tracker.release(this.state.requestId);
      return;
    }
    this.disposeJob();
  }

  private disposeJob(): void {
    if (this.job) {
      this.job.unsubscribe();
      this.job = null;
    }
    if (this.state) {
      this.tracker.release(this.state.requestId);
    }
    this.pollInFlight = false;
  }

  private readRequestId(body: ArrayBuffer): string | undefined {
    if (!body || body.byteLength === 0) {
      return undefined;
    }
    const text = new TextDecoder().decode(body).trim();
    if (!text.startsWith('{')) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(text);
      return parsed && typeof parsed.requestId === 'string' ? parsed.requestId : undefined;
    } catch {
      return undefined;
    }
  }

  private onHubMessage(message: HubMessage): void {
    if (!this.state || !isAwaitingResult(this.state)) {
      return;
    }
    const content = message.content;
    const owner = this.tracker.ownerOf(content as any);
    if (owner !== this.state.requestId) {
      this.reportUnmatched(content);
      return;
    }
    if (!content.requestId) {
      this.reportFallbackMatch('signalr');
    }
    if (message.type === HubMessageType.NotebookGenerated) {
      this.apply({ type: 'ready', generationId: content.generationID, sizeBytes: content.filesize });
    } else {
      this.apply({ type: 'generationFailed', serverErrorCode: content.errorCode });
    }
  }

  private reportUnmatched(content: { [s: string]: any }): void {
    const sameNotebook = matchNotebookResult(this.target, { notebookId: content.notebookId, fileType: content.fileType }) !== 'none';
    if (!sameNotebook || !content.requestId || this.reportedUnmatchedRequestIds.has(content.requestId)) {
      return;
    }
    this.reportedUnmatchedRequestIds.add(content.requestId);
    this.telemetry.notice(this.state, this.now(), 'signalr-mismatch otherRequestId=' + content.requestId);
  }

  private reportFallbackMatch(source: string): void {
    if (this.reportedFallbackMatch) {
      return;
    }
    this.reportedFallbackMatch = true;
    this.telemetry.notice(this.state, this.now(), 'matched-without-requestId source=' + source);
  }

  private pollNow(): void {
    if (!this.state || !isAwaitingResult(this.state) || this.pollInFlight || !this.job) {
      return;
    }
    this.pollInFlight = true;
    const requestId = this.state.requestId;
    this.job.add(
      this.notificationService.GetAllNotification(NOTEBOOK_NOTIFICATION_POLL_COUNT, false, this.params.userId).subscribe({
        next: (response) => {
          this.pollInFlight = false;
          if (!this.state || this.state.requestId !== requestId) {
            return;
          }
          const result = findNotebookNotificationResult(this.target, response.result || [], this.knownNotificationIds);
          if (!result || !this.tracker.claimNotification(result.notificationId, requestId)) {
            return;
          }
          if (!result.requestId) {
            this.reportFallbackMatch('notification-poll');
          }
          if (result.kind === 'ready') {
            this.apply({ type: 'ready', generationId: result.generationId, sizeBytes: result.sizeBytes });
          } else {
            this.apply({ type: 'generationFailed', serverErrorCode: result.serverErrorCode });
          }
        },
        error: () => {
          this.pollInFlight = false;
        },
      })
    );
  }

  private tick(): void {
    if (this.state && (this.state.step === 'requesting' || this.state.step === 'generating' || this.state.step === 'downloading')) {
      this.elapsedText = formatElapsed(this.now() - this.state.startedAt);
      this.cdRef.detectChanges();
    }
  }

  private apply(event: NotebookDownloadEvent): void {
    if (!this.state) {
      return;
    }
    const before = this.state;
    const after = reduceNotebookDownload(before, event);
    if (after === before) {
      return;
    }
    this.state = after;

    if (after.step === 'downloading' && before.step !== 'downloading') {
      this.startFileDownload(after);
    }
    if (after.step === 'done' && this.job) {
      this.job.add(timer(NOTEBOOK_DONE_AUTO_CLOSE_MS).subscribe(() => this.finish()));
    }
    if (after.step === 'error' && !this.isVisible) {
      this.show();
    }
    if (after.step === 'error' && (before.step !== 'error' || before.errorCode !== after.errorCode)) {
      this.telemetry.failure(after, this.now());
    }
    this.cdRef.detectChanges();
  }

  private startFileDownload(state: NotebookDownloadState): void {
    if (!this.job) {
      return;
    }
    const extension = state.fileType === 'PDF' ? '.pdf' : '.zip';
    const fallbackName = 'NotebookID-' + state.notebookId + '-Generated-' + formatFileNameDate(new Date(this.now())) + extension;
    const url = URLUtils.setURLCachingParam(protectedResources.apiBaseUrl.endpoint + '/api/Notebooks/DownloadNotebook/' + state.generationId, true, this.params.userId);
    this.job.add(
      this.fileDownloadService.download(url, fallbackName).subscribe({
        next: (event) => {
          if (event.kind === 'progress') {
            this.apply({ type: 'progress', loadedBytes: event.loadedBytes, totalBytes: event.totalBytes });
          } else {
            this.apply({ type: 'downloaded', fileName: event.fileName, sizeBytes: event.sizeBytes });
          }
        },
        error: (error: HttpErrorResponse) => this.apply({ type: 'downloadFailed', httpStatus: error.status }),
      })
    );
  }
}
